import { ReactNode, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface ModalPortalProps {
  children: ReactNode;
}

/**
 * Mounts modal content into document.body via a React portal so that
 * position:fixed works correctly regardless of any CSS transform, filter,
 * or perspective on ancestor elements. Also locks page scroll and restores
 * focus to the element that triggered the modal.
 */
export function ModalPortal({ children }: ModalPortalProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  if (!containerRef.current) {
    containerRef.current = document.createElement('div');
  }

  useBodyScrollLock();
  useLayoutEffect(() => {
    const container = containerRef.current!;
    const previousFocus = document.activeElement as HTMLElement | null;
    document.body.appendChild(container);

    return () => {
      if (container.parentNode) document.body.removeChild(container);
      previousFocus?.focus();
    };
  }, []);

  return ReactDOM.createPortal(children, containerRef.current);
}
